import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Mic, MicOff, ChevronDown, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Lazy-detect Speech API (avoid crash on unsupported browsers)
const getSpeechRecognition = () =>
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

// Marché LES-LEX : français primaire (fr-FR) + anglais secondaire (en-US)
// Pas d'autres langues — la plateforme cible le marché juridique français.
const LANG_OPTIONS = [
  { code: "fr-FR", flag: "🇫🇷", label: "Français" },
  { code: "en-US", flag: "🇬🇧", label: "English" },
];

const USER_LANG_TO_BCP47 = {
  fr: "fr-FR",
  en: "en-US",
};

const LS_KEY = "leslex_dictate_lang";

/** Détermine la langue par défaut : localStorage > user.language > "fr-FR" */
function pickDefaultLang(userLang) {
  if (typeof window !== "undefined") {
    const saved = window.localStorage?.getItem(LS_KEY);
    if (saved && LANG_OPTIONS.some((o) => o.code === saved)) return saved;
  }
  if (userLang && USER_LANG_TO_BCP47[userLang]) return USER_LANG_TO_BCP47[userLang];
  return "fr-FR";  // toujours français par défaut (marché cible)
}

/**
 * DictateButton — inline microphone for text fields.
 *
 * Props:
 *   onTranscript(text)  — called with each recognized phrase (appended to field)
 *   onInterim(text)     — optional, called with interim (unfinished) transcript
 *   lang                — BCP-47 language tag, default auto-detect (user.language ou fr-FR)
 *   continuous          — keep listening after each phrase (default true)
 *   className           — extra CSS classes
 *   showLangPicker      — afficher le sélecteur de langue (default true)
 *
 * En mode continu, la reconnaissance redémarre automatiquement après chaque phrase,
 * permettant de dicter de longs récits juridiques sans cliquer à nouveau.
 * Un timeout de silence de 4 secondes arrête automatiquement l'écoute.
 *
 * Auto-détection du langue : user.language → localStorage → navigator.language → "fr-FR"
 */
export default function DictateButton({
  onTranscript,
  onInterim,
  lang: langProp,
  continuous = true,
  className = "",
  showLangPicker = true,
}) {
  const { user } = useAuth();
  const userLang = user?.language;
  const [selectedLang, setSelectedLang] = useState(() =>
    langProp || pickDefaultLang(userLang)
  );
  const lang = selectedLang;

  // Quand user.language change (login / re-login), réajuste si pas de localStorage
  useEffect(() => {
    if (!langProp && typeof window !== "undefined" && !window.localStorage?.getItem(LS_KEY)) {
      setSelectedLang(pickDefaultLang(userLang));
    }
  }, [userLang, langProp]);

  const changeLang = useCallback((newLang) => {
    setSelectedLang(newLang);
    try { window.localStorage?.setItem(LS_KEY, newLang); } catch { /* ignore */ }
  }, []);

  const [active, setActive] = useState(false);
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);
  const shouldRestartRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const hasToastedStartRef = useRef(false);
  const SILENCE_TIMEOUT_MS = 4000; // Stop after 4s of silence

  useEffect(() => {
    if (!getSpeechRecognition()) {
      setSupported(false);
    }
    return () => {
      shouldRestartRef.current = false;
      clearTimeout(silenceTimerRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.abort(); } catch { /* ignore */ }
        recognitionRef.current = null;
      }
    };
  }, []);

  // Reset silence timer — called on each speech event
  const resetSilenceTimer = useCallback(() => {
    clearTimeout(silenceTimerRef.current);
    if (shouldRestartRef.current) {
      silenceTimerRef.current = setTimeout(() => {
        // No speech for SILENCE_TIMEOUT_MS → stop
        shouldRestartRef.current = false;
        if (recognitionRef.current) {
          try { recognitionRef.current.stop(); } catch { /* ignore */ }
        }
      }, SILENCE_TIMEOUT_MS);
    }
  }, []);

  const startRecognition = useCallback(async () => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) {
      setSupported(false);
      toast.error("Votre navigateur ne supporte pas la dictée vocale. Utilisez Chrome ou Edge récent.", { duration: 6000 });
      return;
    }

    // Vérification proactive de la permission micro (avant de démarrer le SpeechRecognition)
    // Edge/Chrome : si l'utilisateur a déjà refusé, on le détecte ici plutôt que silencieusement
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // On a juste besoin de vérifier l'accès — on libère immédiatement
        stream.getTracks().forEach((t) => t.stop());
      } catch (mediaErr) {
        const name = mediaErr?.name || "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          toast.error(
            "Microphone bloqué. Cliquez sur le cadenas 🔒 dans la barre d'adresse → Autorisations → Microphone : Autoriser. Puis rechargez la page.",
            { duration: 12000, id: "mic-blocked" },
          );
          return;
        }
        if (name === "NotFoundError" || name === "DevicesNotFoundError") {
          toast.error("Aucun microphone détecté sur votre appareil.", { duration: 6000 });
          return;
        }
        // autres erreurs : on tente quand même de démarrer SpeechRecognition
        console.warn("[DictateButton] getUserMedia warning:", name, mediaErr?.message);
      }
    }
    console.log(`[DictateButton] starting recognition lang=${lang} continuous=${continuous}`);

    // Clean up previous instance
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* ignore */ }
    }

    const recognition = new SpeechRecognition();
    recognition.lang = lang;
    recognition.continuous = continuous;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setActive(true);
      resetSilenceTimer();
      // Toast positif au PREMIER démarrage (pas à chaque auto-restart en mode continu)
      if (!hasToastedStartRef.current) {
        const opt = LANG_OPTIONS.find((o) => o.code === lang) || LANG_OPTIONS[0];
        toast.success(`🎤 Dictée active en ${opt.label} ${opt.flag}`, { duration: 3000, id: "dictate-start" });
        hasToastedStartRef.current = true;
      }
    };

    recognition.onresult = (event) => {
      resetSilenceTimer();

      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          // Deliver final transcript — append to the text field
          if (onTranscript) onTranscript(t);
        } else {
          interim += t;
        }
      }
      // Show interim text (greyed-out preview)
      if (onInterim && interim) onInterim(interim);
    };

    recognition.onerror = (event) => {
      if (event.error === "no-speech") {
        // Silence detected — in continuous mode, restart
        if (shouldRestartRef.current) {
          try { recognition.stop(); } catch { /* ignore */ }
          return;
        }
      }
      // Toast erreurs visibles à l'utilisateur (sauf no-speech qui est normal)
      const ERR_LABEL = {
        "not-allowed":          "Microphone refusé. Cliquez sur le cadenas 🔒 dans la barre d'adresse → autorisez le micro pour leslex.fr.",
        "service-not-allowed":  "Le service de reconnaissance vocale est bloqué. Vérifiez votre antivirus / pare-feu.",
        "network":              "Erreur réseau pour la dictée. Vérifiez votre connexion (la reconnaissance vocale utilise les serveurs Google).",
        "audio-capture":        "Aucun microphone détecté. Branchez un micro ou activez celui de votre ordinateur.",
        "language-not-supported": `Langue ${lang} non supportée par votre navigateur. Basculez sur l'autre langue (FR/EN) via le menu ▾.`,
        "bad-grammar":          "Erreur de grammaire vocale (rare).",
        "aborted":              null, // user clicked stop — silent
        "no-speech":            null, // silence — already handled
      };
      const userMsg = ERR_LABEL[event.error];
      if (userMsg) {
        toast.error(userMsg, { duration: 8000, id: `dictate-err-${event.error}` });
        console.warn("[DictateButton] error:", event.error);
      } else if (event.error && !["aborted", "no-speech"].includes(event.error)) {
        console.warn("[DictateButton] unknown error:", event.error);
        toast.error(`Erreur dictée : ${event.error}. Réessayez ou changez de langue.`, { duration: 6000 });
      }
      shouldRestartRef.current = false;
      setActive(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      // In continuous mode, auto-restart unless explicitly stopped
      if (shouldRestartRef.current && continuous) {
        try {
          recognition.start();
          return; // Stay active
        } catch {
          // Failed to restart — fall through to stop
        }
      }
      setActive(false);
      recognitionRef.current = null;
      clearTimeout(silenceTimerRef.current);
    };

    recognitionRef.current = recognition;
    shouldRestartRef.current = continuous;
    setActive(true);

    try {
      recognition.start();
    } catch (err) {
      console.warn("[DictateButton] start failed:", err);
      toast.error("Impossible de démarrer la dictée. Cliquez à nouveau ou rechargez la page (Ctrl+Shift+R).", { duration: 6000 });
      setActive(false);
      shouldRestartRef.current = false;
      recognitionRef.current = null;
    }
  }, [lang, continuous, onTranscript, onInterim, resetSilenceTimer]);

  const stopRecognition = useCallback(() => {
    shouldRestartRef.current = false;
    clearTimeout(silenceTimerRef.current);
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    setActive(false);
    hasToastedStartRef.current = false; // reset pour prochaine session
  }, []);

  const toggle = useCallback(() => {
    if (active) stopRecognition();
    else startRecognition();
  }, [active, startRecognition, stopRecognition]);

  if (!supported) return null;

  const currentOption = LANG_OPTIONS.find((o) => o.code === lang) || LANG_OPTIONS[0];
  const label = active
    ? `Arrêter la dictée (${currentOption.label})`
    : `Dicter en ${currentOption.label}`;

  // Si le picker est désactivé : juste le bouton micro simple
  if (!showLangPicker) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant={active ? "destructive" : "outline"}
              size="icon"
              className={`relative h-9 w-9 rounded-full ${className}`}
              onClick={toggle}
              aria-label={label}
            >
              {active ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              {active && (
                <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-40 pointer-events-none" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Avec picker : bouton mic + petit menu drapeau au survol
  return (
    <div className={`inline-flex items-center gap-0.5 ${className}`}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant={active ? "destructive" : "outline"}
              size="icon"
              className="relative h-9 w-9 rounded-full"
              onClick={toggle}
              aria-label={label}
            >
              {active ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              {active && (
                <span className="absolute inset-0 rounded-full animate-ping bg-red-400 opacity-40 pointer-events-none" />
              )}
              {/* Petit drapeau en bas-droite — indique la langue actuelle */}
              <span className="absolute -bottom-1 -right-1 text-[10px] leading-none bg-background border rounded-full w-4 h-4 flex items-center justify-center shadow-sm">
                {currentOption.flag}
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{label}</p>
            <p className="text-[10px] opacity-70 mt-0.5">Cliquez sur le drapeau pour changer de langue</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Sélecteur de langue */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full"
            aria-label="Changer la langue de dictée"
            disabled={active}
          >
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel className="text-xs">Langue de la dictée</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {LANG_OPTIONS.map((opt) => (
            <DropdownMenuItem
              key={opt.code}
              onClick={() => changeLang(opt.code)}
              className="text-sm"
            >
              <span className="mr-2">{opt.flag}</span>
              <span className="flex-1">{opt.label}</span>
              {opt.code === lang && <Check className="h-3.5 w-3.5 text-primary" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
